import { useSyncExternalStore } from "react";
import type { OnboardingStep } from "@/api/onboarding";

declare global {
  interface Window {
    /** The utility surface a window carries; absent on display windows. */
    __DENKER_SURFACE__?: "setup" | "pin";
    __DENKER_SETUP_ACTIVE__?: boolean;
    __DENKER_SETUP_GENERATION__?: number;
    __DENKER_SETUP_BOUNDED__?: boolean;
  }
}

export const isSetupSurface = () => window.__DENKER_SURFACE__ === "setup";

/**
 * A display's own window, not a utility surface (setup, pin) that shares its
 * display. Presentations for a display run only there, so they run once.
 */
export const isDisplayWindow = () => window.__DENKER_SURFACE__ === undefined;

/** Public practice and farewell stay in the full display app windows. */
export function isPrivateSetupStep(step: OnboardingStep | null): boolean {
  return (
    step === null ||
    [
      "desktop_intro",
      "desktop_permissions",
      "desktop_perm_voice_quality",
      "desktop_perm_voice",
      "desktop_perm_hotkeys",
      "desktop_perm_vision",
      "desktop_runtime_setup",
      "desktop_browser_setup",
    ].includes(step)
  );
}

function subscribe(callback: () => void) {
  window.addEventListener("denker:setup-window-changed", callback);
  window.addEventListener("denker:first-run-geometry-changed", callback);
  return () => {
    window.removeEventListener("denker:setup-window-changed", callback);
    window.removeEventListener("denker:first-run-geometry-changed", callback);
  };
}

export function useSetupWindowState() {
  useSyncExternalStore(
    subscribe,
    () =>
      `${window.__DENKER_SETUP_ACTIVE__}:${window.__DENKER_SETUP_GENERATION__}:${window.__DENKER_DISPLAY_ID__}`,
  );
  return {
    active: window.__DENKER_SETUP_ACTIVE__ === true,
    generation: window.__DENKER_SETUP_GENERATION__ ?? 0,
    displayId: window.__DENKER_DISPLAY_ID__ ?? null,
  };
}
